
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { HeroSection } from "./landing/HeroSection";
import { ProblemSolution } from "./landing/ProblemSolution";
import { HowItWorks } from "./landing/HowItWorks";
import { FooterCTA } from "./landing/FooterCTA";

// "/" ye do kaam karta hai:
// 1. Normal visit (bina ?token=) -> marketing landing page dikhta hai.
// 2. SSO redirect (/?token=<revyniqx jwt>, jo /sso/callback se yahan
//    forward hua) -> revyniqx ke token ko coach ke access_token se
//    exchange karta hai, phir /simulator pe bhej deta hai.

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithSSOToken } = useAuth();

  const hasRun = useRef(false);
  const revyniqxToken = searchParams.get("token");

  const [status, setStatus] = useState(revyniqxToken ? "working" : "idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!revyniqxToken) return;
    if (hasRun.current) return;
    hasRun.current = true;

    const exchangeToken = async () => {
      try {
        // Step 1: revyniqx token -> coach access token
        const response = await axios.post(`${API}/sso/login`, {
          token: revyniqxToken,
        });

        const accessToken =
          response.data.access_token ||
          response.data.token ||
          response.data.accessToken;

        if (!accessToken) {
          console.error("[SSO] /sso/login response had no access token:", response.data);
          throw new Error("Sign-in failed: server did not return an access token.");
        }

        // Step 2: store + hydrate user session
        const ok = await loginWithSSOToken(accessToken);
        if (!ok) {
          throw new Error("Sign-in failed: could not verify session after login.");
        }

        // Step 3: token URL se hata ke app ke andar bhej do
        navigate("/simulator", { replace: true });
      } catch (err) {
        console.error("[SSO] Callback exchange failed:", err);
        setErrorMessage(
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          "Sign-in failed. Please try again."
        );
        setStatus("error");
      }
    };

    exchangeToken();
  }, [revyniqxToken, loginWithSSOToken, navigate]);

  if (status === "working") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 rounded-full border-slate-200 border-t-slate-900 animate-spin"></div>
          <p className="font-medium text-slate-600">Signing you in...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="max-w-sm px-6 text-center">
          <p className="font-semibold text-red-600">{errorMessage}</p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="px-4 py-2 mt-6 text-sm font-medium text-white rounded-lg bg-slate-900 hover:bg-slate-800"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <HeroSection mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <ProblemSolution />
      <HowItWorks />
      <FooterCTA />
    </div>
  );
}